import Vapi from '@vapi-ai/web';
import * as Sentry from '@sentry/browser';

document.addEventListener('DOMContentLoaded', () => {
    const vapi = new Vapi('6e91e0c2-e25a-40ed-981c-324220f9dffd');

    // Demo Sales Agent Configuration
    const assistantId = '521c9515-7c7f-483e-85b6-307e6f444f59';

    const btn = document.getElementById('dispatch-btn');
    const voiceStateIcon = document.getElementById('voice-state-icon');
    const voiceStateText = document.getElementById('voice-state-text');
    const logContainer = document.getElementById('execution-log');

    const badges = {
        crm: document.getElementById('badge-crm'),
        calendar: document.getElementById('badge-calendar'),
        sms: document.getElementById('badge-sms'),
    };

    let isActive = false;
    let shouldOpenModalOnEnd = false;

    function addLog(message, isSystem = false) {
        const time = new Date().toLocaleTimeString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        const div = document.createElement('div');
        div.className = isSystem ? 'text-zinc-500' : 'text-zinc-300';
        div.textContent = `[${time}] ${message}`;
        logContainer.appendChild(div);
        logContainer.scrollTop = logContainer.scrollHeight;
    }

    function setVoiceState(state) {
        if (state === 'idle') {
            voiceStateIcon.innerHTML = '<div class="h-3 w-3 bg-zinc-500 rounded-full"></div>';
            voiceStateIcon.className =
                'h-12 w-12 rounded-full border-2 border-zinc-700 bg-zinc-800 flex items-center justify-center transition-all duration-300';
            voiceStateText.textContent = 'Idle';
            voiceStateText.className = 'text-xs font-bold text-zinc-500 uppercase tracking-widest';
        } else if (state === 'listening') {
            voiceStateIcon.innerHTML =
                '<div class="h-4 w-4 bg-emerald-400 rounded-full animate-ping"></div><div class="absolute h-3 w-3 bg-emerald-500 rounded-full"></div>';
            voiceStateIcon.className =
                'relative h-12 w-12 rounded-full border-2 border-emerald-500/30 bg-emerald-900/20 flex items-center justify-center transition-all duration-300';
            voiceStateText.textContent = 'Listening';
            voiceStateText.className = 'text-xs font-bold text-emerald-400 uppercase tracking-widest';
        } else if (state === 'speaking') {
            voiceStateIcon.innerHTML =
                '<div class="flex gap-1 items-center"><div class="w-1 h-3 bg-blue-400 animate-pulse"></div><div class="w-1 h-5 bg-blue-400 animate-pulse delay-75"></div><div class="w-1 h-3 bg-blue-400 animate-pulse delay-150"></div></div>';
            voiceStateIcon.className =
                'h-12 w-12 rounded-full border-2 border-blue-500/30 bg-blue-900/20 flex items-center justify-center transition-all duration-300';
            voiceStateText.textContent = 'Speaking';
            voiceStateText.className = 'text-xs font-bold text-blue-400 uppercase tracking-widest';
        }
    }

    function triggerBadge(badgeName) {
        const badge = badges[badgeName];
        if (!badge) return;
        badge.classList.remove('bg-zinc-950', 'border-zinc-800', 'text-zinc-500');
        badge.classList.add(
            'bg-emerald-900/30',
            'border-emerald-500',
            'text-emerald-400',
            'shadow-[0_0_10px_rgba(16,185,129,0.2)]',
        );
        setTimeout(() => {
            badge.classList.remove(
                'bg-emerald-900/30',
                'border-emerald-500',
                'text-emerald-400',
                'shadow-[0_0_10px_rgba(16,185,129,0.2)]',
            );
            badge.classList.add('bg-zinc-950', 'border-zinc-800', 'text-zinc-500');
        }, 2000);
    }

    if (btn) {
        btn.addEventListener('click', () => {
            if (isActive) {
                vapi.stop();
            } else {
                Sentry.startSpan(
                    {
                        op: 'ui.click',
                        name: 'Start AI Dispatch',
                    },
                    (span) => {
                        vapi.start(assistantId);
                        btn.textContent = 'TERMINATE';
                        btn.className =
                            'w-full py-4 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold tracking-widest uppercase transition-all duration-300 shadow-[0_0_20px_rgba(225,29,72,0.4)]';
                        isActive = true;
                        addLog('Initiating AI Voice Engine...', true);
                        if (span) {
                            span.setAttribute('assistant_id', assistantId);
                        }
                    },
                );
            }
        });
    }

    vapi.on('call-start', () => {
        addLog('Call connected. Agent is active.', true);
        setVoiceState('listening');
    });

    vapi.on('call-end', () => {
        isActive = false;
        if (btn) {
            btn.textContent = 'START AI DISPATCH';
            btn.className =
                'w-full py-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold tracking-widest uppercase transition-all duration-300 shadow-[0_0_20px_rgba(5,150,105,0.4)]';
        }
        setVoiceState('idle');
        addLog('Call disconnected.', true);

        if (shouldOpenModalOnEnd) {
            if (window.openMeetingModal) {
                window.openMeetingModal();
            }
            shouldOpenModalOnEnd = false;
        }
    });

    vapi.on('speech-start', () => {
        setVoiceState('speaking');
    });

    vapi.on('speech-end', () => {
        setVoiceState('listening');
    });

    vapi.on('message', (message) => {
        if (message.type === 'transcript' && message.transcriptType === 'final') {
            const prefix = message.role === 'user' ? 'User: ' : 'AI: ';
            let text = message.transcript.toLowerCase();
            let sentimentAlert = '';

            if (message.role === 'user') {
                const angryKeywords = [
                    'angry',
                    'upset',
                    'unhappy',
                    'terrible',
                    'worst',
                    'manager',
                    'frustrated',
                    'pissed',
                ];
                const urgentKeywords = ['emergency', 'leak', 'flood', 'fire', 'immediate', 'urgent', 'now', 'help'];

                if (angryKeywords.some((k) => text.includes(k))) {
                    sentimentAlert = ' 😡 [FRUSTRATION DETECTED]';
                } else if (urgentKeywords.some((k) => text.includes(k))) {
                    sentimentAlert = ' 🚨 [EMERGENCY DETECTED]';
                }
            }

            addLog(prefix + message.transcript + sentimentAlert);

            if (sentimentAlert) {
                voiceStateIcon.classList.add('ring-4', 'ring-rose-500', 'animate-pulse');
                setTimeout(() => {
                    voiceStateIcon.classList.remove('ring-4', 'ring-rose-500', 'animate-pulse');
                }, 3000);
            }
        } else if (message.type === 'function-call' || message.type === 'tool-calls') {
            let fnName = '';
            if (message.functionCall && message.functionCall.name) {
                fnName = message.functionCall.name.toLowerCase();
            } else if (message.toolCalls && message.toolCalls.length > 0) {
                fnName = message.toolCalls[0].function.name.toLowerCase();
            }

            if (fnName) {
                addLog('Executing integration: ' + fnName, true);
                if (fnName.includes('crm') || fnName.includes('lead') || fnName.includes('contact')) {
                    triggerBadge('crm');
                } else if (fnName.includes('book') || fnName.includes('calendar') || fnName.includes('appointment')) {
                    triggerBadge('calendar');
                    shouldOpenModalOnEnd = true;
                } else if (fnName.includes('sms') || fnName.includes('message') || fnName.includes('text')) {
                    triggerBadge('sms');
                } else {
                    triggerBadge('crm');
                }
            }
        }
    });

    vapi.on('error', (e) => {
        console.error(e);

        let errorString;
        if (typeof e === 'string') {
            errorString = e;
        } else if (e?.message) {
            errorString = e.message;
        } else if (e?.error?.message) {
            errorString = e.error.message;
        } else if (e?.error) {
            errorString = String(e.error);
        } else if (e?.name) {
            errorString = e.name;
        } else {
            try {
                errorString = JSON.stringify(e) !== '{}' ? JSON.stringify(e) : 'Unknown Error';
            } catch {
                errorString = 'Unknown Error';
            }
        }

        if (e instanceof Error) {
            Sentry.captureException(e);
        } else {
            Sentry.captureException(new Error(`Vapi Error: ${errorString}`), { extra: { vapiError: e } });
        }

        addLog('Error: ' + errorString, true);
        isActive = false;
        setVoiceState('idle');
        if (btn) {
            btn.textContent = 'START AI DISPATCH';
            btn.className =
                'w-full py-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold tracking-widest uppercase transition-all duration-300 shadow-[0_0_20px_rgba(5,150,105,0.4)]';
        }

        // Fallback UI for common errors
        const errorMsgLower = String(errorString || '').toLowerCase();
        if (
            errorMsgLower.includes('microphone') ||
            errorMsgLower.includes('permission') ||
            errorMsgLower.includes('device') ||
            errorMsgLower.includes('notallowederror') ||
            errorMsgLower.includes('notreadableerror')
        ) {
            if (window.openErrorModal) {
                window.openErrorModal(
                    'Microphone access was denied or no microphone was found. Please allow microphone access in your browser to try the live demo, or click "Contact Sales" to book a meeting directly.',
                );
            } else {
                alert(
                    'Microphone access was denied or no microphone was found. Please allow microphone access in your browser to try the live demo, or click "Contact Sales" to book a meeting directly.',
                );
            }
        } else if (errorMsgLower.includes('network') || errorMsgLower.includes('connection')) {
            if (window.openErrorModal) {
                window.openErrorModal(
                    'A network error occurred while connecting to the AI. Please check your connection and try again.',
                );
            } else {
                alert(
                    'A network error occurred while connecting to the AI. Please check your connection and try again.',
                );
            }
        }
    });
});
